import { useEffect } from "react";
import { Stack, useRouter, useNavigation } from "expo-router";
import { Pressable, View, Text, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";

/**
 * Super Admin layout - screens for super_admin users only
 * Protected route: redirects non-super-admin users to home
 */
export default function SuperAdminLayout() {
  const router = useRouter();
  const navigation = useNavigation();
  const colors = useThemeColors();
  const { roles, isLoading, isInitialized } = useAuth();

  // Check if user has super admin access
  const hasSuperAdminAccess = roles.isSuperAdmin;

  // Redirect non-super-admin users to home
  useEffect(() => {
    if (isInitialized && !isLoading && !hasSuperAdminAccess) {
      // User is not a super admin, redirect to home
      router.replace("/(tabs)");
    }
  }, [isInitialized, isLoading, hasSuperAdminAccess, router]);

  /**
   * Handle back navigation safely
   * If there's no screen to go back to, navigate to home tabs
   */
  const handleBack = () => {
    if (navigation.canGoBack()) {
      router.back();
    } else {
      // No screen to go back to, navigate to home
      router.replace("/(tabs)");
    }
  };

  // Show loading while checking auth
  if (!isInitialized || isLoading) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color="#DC2626" />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </View>
    );
  }

  // Don't render super admin content for non-super-admins (will redirect via useEffect)
  if (!hasSuperAdminAccess) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name="lock-closed" size={48} color="#DC2626" />
        <Text className="mt-4 text-lg font-medium" style={{ color: colors.text }}>
          Geen toegang
        </Text>
        <Text className="mt-2 text-center px-8" style={{ color: colors.textSecondary }}>
          Je hebt geen toegang tot het super admin panel.
        </Text>
      </View>
    );
  }

  return (
    <Stack
      screenOptions={{
        headerShown: true,
        headerStyle: { backgroundColor: colors.surface },
        headerTintColor: "#DC2626",
        headerTitleStyle: { color: colors.text },
        headerLeft: () => (
          <Pressable onPress={handleBack} className="mr-2">
            <Ionicons name="arrow-back" size={24} color="#DC2626" />
          </Pressable>
        ),
      }}
    >
      <Stack.Screen
        name="index"
        options={{ title: "Super Admin" }}
      />
      <Stack.Screen
        name="users"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="weekends"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="logs"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="notifications"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="pending-members"
        options={{ headerShown: false }}
      />
    </Stack>
  );
}
